import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Loader2, AlertCircle, FileText, Save, X, Building2 } from 'lucide-react';
import { kycService } from '@services/kycService';
import { clientService } from '@services/clientService';
import { companyService } from '@services/companyService';
import CustomSelect from '../shared/CustomSelect';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

const KycUploadPage = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [client, setClient] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [requirements, setRequirements] = useState([]);
    const [loadingRequirements, setLoadingRequirements] = useState(false);
    const [selectedRequirement, setSelectedRequirement] = useState('');
    const [documentNumber, setDocumentNumber] = useState('');
    const [notes, setNotes] = useState('');
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchClient();
        fetchCompanies();
    }, [clientId]);

    useEffect(() => {
        if (client && selectedCompanyId) {
            fetchRequirements();
        } else {
            setRequirements([]);
        }
    }, [client, selectedCompanyId]);

    const fetchClient = async () => {
        try {
            const response = await clientService.getById(clientId);
            const clientData = response.data || response;
            setClient(clientData);
        } catch (err) {
            console.error('Error fetching client:', err);
            setError('Failed to load client details');
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await companyService.getAll();
            const data = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
            setCompanies(data);
        } catch (err) {
            console.error('Error fetching companies:', err);
        }
    };

    const fetchRequirements = async () => {
        try {
            setLoadingRequirements(true);
            setSelectedRequirement('');
            const clientType = client?.clientType;

            let response;
            if (clientType) {
                response = await kycService.kycRequirements.getByCompanyAndClientType(selectedCompanyId, clientType);
            } else {
                response = await kycService.kycRequirements.getByCompany(selectedCompanyId);
            }

            const reqs = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
            setRequirements(reqs);
        } catch (err) {
            console.error('Error fetching KYC requirements:', err);
            setError('Failed to load KYC types');
        } finally {
            setLoadingRequirements(false);
        }
    };

    const getAutoDocumentNumber = (requirementId) => {
        if (!client) return '';
        const req = requirements.find(r => r.id === requirementId);
        if (!req) return '';
        const typeMap = {
            'NATIONAL_ID': client.nationalId,
            'KRA_PIN': client.kraPin,
            'PASSPORT': client.passportNumber,
            'TAX_REGISTRATION': client.taxIdNumber,
        };
        return typeMap[req.type] || '';
    };

    const handleRequirementChange = (value) => {
        setSelectedRequirement(value);
        const autoNumber = getAutoDocumentNumber(value);
        if (autoNumber) {
            setDocumentNumber(autoNumber);
            setNotes('Auto-filled from client details');
        } else {
            setDocumentNumber('');
            setNotes('');
        }
    };

    const validateAndSetFile = (selected) => {
        if (!selected) return;
        if (!ACCEPTED_TYPES.includes(selected.type)) {
            setError('Only PDF, JPG, JPEG, and PNG files are accepted');
            return;
        }
        if (selected.size > MAX_SIZE) {
            setError('File size must be less than 10MB');
            return;
        }
        setFile(selected);
        setError('');
    };

    const handleFileChange = (e) => {
        validateAndSetFile(e.target.files[0]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        validateAndSetFile(droppedFile);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedRequirement || !file) return;

        try {
            setSubmitting(true);
            setError('');

            const response = await kycService.clientKyc.submitWithFile(
                clientId,
                selectedRequirement,
                file,
                documentNumber,
                notes
            );

            if (response.success) {
                navigate(`/clients/${clientId}`, { state: { activeTab: 'kyc' } });
            } else {
                setError(response.message || 'Failed to submit document');
            }
        } catch (err) {
            console.error('Error submitting KYC document:', err);
            setError(err.message || 'Failed to submit document');
        } finally {
            setSubmitting(false);
        }
    };

    // Group requirements: mandatory first, then optional
    const mandatoryReqs = requirements.filter(r => r.mandatory);
    const optionalReqs = requirements.filter(r => !r.mandatory);
    const requirementOptions = [
        ...mandatoryReqs.map(req => ({
            value: req.id,
            label: `${req.name} (Required)`
        })),
        ...(mandatoryReqs.length > 0 && optionalReqs.length > 0 ? [{ value: '__separator__', label: '── Optional ──', disabled: true }] : []),
        ...optionalReqs.map(req => ({
            value: req.id,
            label: req.name
        }))
    ];

    const companyOptions = companies.map(c => ({
        value: c.id,
        label: c.name
    }));

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`/clients/${clientId}`, { state: { activeTab: 'kyc' } })}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">Add KYC Document</h2>
                        {client && (
                            <p className="text-sm text-gray-500 mt-1">
                                For: {client.firstName ? `${client.firstName} ${client.lastName || ''}`.trim() : client.companyName || client.name}
                                {client.clientType && (
                                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                                        {client.clientType}
                                    </span>
                                )}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    {error}
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Document Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Company Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <span className="flex items-center gap-1.5">
                                        <Building2 className="w-4 h-4" />
                                        Company <span className="text-red-500">*</span>
                                    </span>
                                </label>
                                <CustomSelect
                                    value={selectedCompanyId}
                                    onChange={(value) => {
                                        setSelectedCompanyId(value);
                                        setSelectedRequirement('');
                                    }}
                                    options={companyOptions}
                                    placeholder="Select company for this KYC"
                                    searchable
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Select the company this document is being submitted for
                                </p>
                            </div>

                            {/* Requirement Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    KYC Type <span className="text-red-500">*</span>
                                </label>
                                {!selectedCompanyId ? (
                                    <div className="text-sm text-gray-400 py-2 italic">
                                        Select a company first
                                    </div>
                                ) : loadingRequirements ? (
                                    <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Loading KYC types...
                                    </div>
                                ) : requirements.length === 0 ? (
                                    <div className="text-sm text-amber-600 py-2">
                                        No requirements configured for this company
                                    </div>
                                ) : (
                                    <CustomSelect
                                        value={selectedRequirement}
                                        onChange={handleRequirementChange}
                                        options={requirementOptions}
                                        placeholder="Select KYC type"
                                        searchable
                                    />
                                )}
                            </div>

                            {/* Document Number */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Document Number
                                </label>
                                <input
                                    type="text"
                                    value={documentNumber}
                                    onChange={(e) => setDocumentNumber(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., 12345678"
                                />
                            </div>
                        </div>

                        {/* File Upload */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Document File <span className="text-red-500">*</span>
                            </label>
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                                    dragging
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-300'
                                }`}
                            >
                                {file ? (
                                    <div className="flex items-center justify-center gap-3">
                                        <FileText className="w-6 h-6 text-blue-600" />
                                        <span className="text-sm text-gray-900 font-medium">{file.name}</span>
                                        <span className="text-xs text-gray-500">
                                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setFile(null)}
                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="cursor-pointer"
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                        <div className="space-y-2">
                                            <Upload className={`w-10 h-10 mx-auto ${dragging ? 'text-blue-500' : 'text-gray-400'}`} />
                                            <p className="text-sm text-gray-600">
                                                {dragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                PDF, JPG, JPEG, PNG (max 10MB)
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes
                            </label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows="3"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Optional notes about this document..."
                            />
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex items-center gap-3 pt-6 border-t">
                        <button
                            type="submit"
                            disabled={submitting || !selectedRequirement || !file || !selectedCompanyId}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Add Document
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(`/clients/${clientId}`, { state: { activeTab: 'kyc' } })}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default KycUploadPage;
